import type { ListItemProps } from "@studio3t/generic-components";
import { useMemo, useRef } from "react";
import { schemaApi } from "@/api/client";
import { FooterPicker } from "./FooterPicker";
import { detectSchemaEncoding, type SchemaEncoding } from "./schemaEncoding";

// Server-side conversion into the other encoding; null on failure —
// conversion is best-effort, a failed call leaves the content as-is.
async function convertSchema(
  from: SchemaEncoding,
  to: SchemaEncoding,
  body: string,
): Promise<string | null> {
  try {
    const response = await schemaApi.convert({
      source: { schema_encoding: from, body },
      target_encoding: to,
      // json→avro needs a record name for the generated Avro schema —
      // old /studio derived it from the saved schema's name; unsaved
      // content falls back to the same default.
      ...(to === "avro" ? { options: { name: "Record" } } : {}),
    });
    return response.schema;
  } catch (error) {
    console.error("Schema conversion failed:", error);
    return null;
  }
}

// The picker's options, spelled as the explorer spells schema encodings.
const SCHEMA_ENCODING_OPTIONS: readonly SchemaEncoding[] = ["json", "avro"];
export const SCHEMA_ENCODING_LABELS: Record<SchemaEncoding, string> = {
  json: "JSON",
  avro: "AVRO",
};

/** The picker's accessible name: its visible text is the bare encoding. */
export function schemaEncodingName(encoding: SchemaEncoding): string {
  return `Schema encoding: ${SCHEMA_ENCODING_LABELS[encoding]}`;
}

// The schema editor's encoding picker (Figma Bridge_PipelineCode StatusBar
// 16389-131398; old /studio's "→ JSON" / "→ Avro" convert button): the
// trigger reads the detected encoding, picking the other one converts the
// content server-side. While the content is empty or unparsable the picker
// stays visible but disabled; its label keeps the last detected encoding so
// it doesn't flicker mid-edit (fresh editor defaults to the Avro
// placeholder's). A conversion in flight (tens of milliseconds) leaves the
// trigger as it is: swapping the label for "..." narrowed it and shoved the
// variant picker beside it sideways, and greying it out flashed; the
// re-entrancy guard already ignores a pick made meanwhile.
export function SchemaEncodingDropdown({
  schemaDocument,
  setSchemaDocument,
}: {
  schemaDocument: string;
  setSchemaDocument: (value: string) => void;
}) {
  // Same re-entrancy guard as old /studio's useSchemaState.
  const convertingRef = useRef(false);

  const encoding = useMemo(
    () => detectSchemaEncoding(schemaDocument),
    [schemaDocument],
  );
  const lastEncodingRef = useRef<SchemaEncoding>("avro");
  if (encoding !== null) lastEncodingRef.current = encoding;
  const labelEncoding = encoding ?? lastEncodingRef.current;

  const convertTo = async (target: SchemaEncoding) => {
    if (encoding === null || target === encoding) return;
    if (convertingRef.current) return;
    convertingRef.current = true;
    const converted = await convertSchema(encoding, target, schemaDocument);
    if (converted !== null) setSchemaDocument(converted);
    convertingRef.current = false;
  };

  const items: ListItemProps[][] = [
    SCHEMA_ENCODING_OPTIONS.map((value) => ({
      type: "text" as const,
      id: `schema-encoding-${value}`,
      value,
      label: SCHEMA_ENCODING_LABELS[value],
      selected: { value: value === labelEncoding, icon: "dot" as const },
      onClick: () => void convertTo(value),
    })),
  ];
  return (
    <FooterPicker
      label={SCHEMA_ENCODING_LABELS[labelEncoding]}
      name={schemaEncodingName(labelEncoding)}
      items={items}
      disabled={encoding === null}
      testId="schema-encoding"
    />
  );
}
